import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  Product,
  ProductVariant,
  AddToCartEvent,
  LOW_STOCK_THRESHOLD,
  PREPURCHASE_SOLD_OUT_MESSAGE,
  VARIANT_LABELS,
  getPrepurchaseStock,
  isEarlyAccessProduct,
  isPrepurchaseSoldOut,
} from '../../models/product.model';
import { listRequirementText, vipBadgeText } from '../../perron-points/perron-points-copy';
import { earlyAccessThresholdOf } from '../../perron-points/early-access-gate';
import { PointsService } from '../../services/points.service';
import {
  ShoppingCart,
  Bell,
  BellOff,
  Minus,
  Plus,
  Check,
  CalendarClock,
  Columns3,
  LucideIconData,
} from 'lucide-angular';
import { BadgeComponent } from '../badge/badge.component';
import { VariantBadgeComponent } from '../variant-badge/variant-badge.component';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { PpCoinComponent } from '../pp-coin/pp-coin.component';
import { WishlistButtonComponent } from '../wishlist-button/wishlist-button.component';
import { TooltipDirective } from '../../directives/tooltip.directive';
import { formatPrice, formatOriginalPrice, calcDiscountPercent, isSingleProduct, formatCondition, getConditionTone } from '../../utils/product.utils';
import { CompareService } from '../../services/compare.service';
import { StockAlertStore } from '../../services/stock-alert.store';
import { ToastService } from '../../services/toast.service';

export type CardViewMode = 'grid' | 'list';
export type CardVariant = 'default' | 'wishlist';

@Component({
  selector: 'app-product-card',
  imports: [
    BadgeComponent,
    VariantBadgeComponent,
    ButtonComponent,
    IconComponent,
    TooltipDirective,
    PpCoinComponent,
    WishlistButtonComponent,
  ],
  templateUrl: './product-card.component.html',
  styleUrl: './product-card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class ProductCardComponent {
  private readonly compareService = inject(CompareService);
  private readonly stockAlertStore = inject(StockAlertStore);
  private readonly toastService = inject(ToastService);
  private readonly pointsService = inject(PointsService);

  protected readonly prepurchaseSoldOutMessage = PREPURCHASE_SOLD_OUT_MESSAGE;

  product = input.required<Product>();
  viewMode = input<CardViewMode>('grid');
  variant = input<CardVariant>('default');
  /**
   * Muestra el requisito «Requiere {umbral}+ Perrón Points» bajo el badge VIP.
   * Solo lo activa la lista de precompras (D5, Perrón Points v2) — el resto de
   * superficies que reutilizan esta card (catálogo, relacionados, etc.) no lo
   * piden y no deben mostrarlo.
   */
  showEaRequirement = input(false);

  addToCart = output<AddToCartEvent>();
  zoomOpen = output<Product>();
  notifyRequest = output<Product>();
  notifyCancel = output<Product>();
  cardClick = output<Product>();
  /**
   * Producto en precompra: el padre debe abrir el `PrepurchaseModalService`
   * con este producto. Flujo paralelo al carrito — no pasa por `addToCart`.
   */
  reserveClick = output<Product>();

  protected readonly cartIcon: LucideIconData = ShoppingCart;
  protected readonly bellIcon: LucideIconData = Bell;
  protected readonly bellOffIcon: LucideIconData = BellOff;
  protected readonly minusIcon: LucideIconData = Minus;
  protected readonly plusIcon: LucideIconData = Plus;
  protected readonly checkIcon: LucideIconData = Check;
  protected readonly calendarIcon: LucideIconData = CalendarClock;
  protected readonly compareIcon: LucideIconData = Columns3;

  protected quantity = signal(1);
  protected justAdded = signal(false);

  /**
   * Estado "ya estoy suscrito" derivado del `StockAlertStore` (localStorage),
   * la única fuente también en la lista de deseos (B18; el input `notifyActive`
   * de la pestaña vieja se retiró en W2 de B10).
   *
   * Los endpoints de listado no devuelven `stock_alert.has_alert` (solo el
   * de PDP), así que para grids/carruseles el store es la única fuente de
   * verdad disponible — y es persistente entre navegaciones y F5.
   */
  protected readonly isNotifyActive = computed(() => this.stockAlertStore.hasAlert(this.product().id));

  protected isSingle = computed(() => isSingleProduct(this.product()));

  protected hasSecondImage = computed(() => !!this.product().imageUrl2);

  /**
   * Precompra: tratamos `prepurchase_stock > 0` como "hay disponibilidad".
   * El `stock` regular puede ser 0 para productos en precompra (aún no
   * existen físicamente), por eso `isOutOfStock` y `isLowStock` no aplican
   * a estas cards y el template esconde el CTA de aviso de stock.
   */
  protected isOutOfStock = computed(() => {
    const p = this.product();
    if (p.isPreSale) return getPrepurchaseStock(p) <= 0;
    return p.stock === 0;
  });

  /** Precompra con cupos a 0: el CTA "Reservar" se sustituye por
   * "Precompra Agotada" en todas las superficies. */
  protected isPrepurchaseSoldOut = computed(() =>
    isPrepurchaseSoldOut(this.product()),
  );

  /** Badge «Acceso VIP» (Perrón Points) — desde R7 no depende de precompra. */
  protected isEarlyAccess = computed(() => isEarlyAccessProduct(this.product()));

  /**
   * Umbral efectivo del producto: el resuelto por el servidor
   * (`early_access_threshold`) y, si el payload no lo trae (JSON cacheado de
   * antes del deploy), el último umbral conocido de usuario o el fallback.
   */
  protected readonly eaThreshold = computed(
    () =>
      earlyAccessThresholdOf(this.product(), this.pointsService.displayThreshold())
      ?? this.pointsService.displayThreshold(),
  );

  /** Badge de identidad VIP, con la variante de umbral 0 («Solo registrados»). */
  protected readonly eaBadgeText = computed(() => vipBadgeText(this.eaThreshold()));

  /** Requisito visible de la lista de precompras (D5), con su variante de umbral 0. */
  protected readonly eaRequirementText = computed(() => listRequirementText(this.eaThreshold()));

  protected isLowStock = computed(() => {
    const p = this.product();
    if (p.isPreSale) return false;
    const stock = p.stock;
    return stock > 0 && stock <= LOW_STOCK_THRESHOLD;
  });

  protected canDecrement = computed(() => this.quantity() > 1);
  protected canIncrement = computed(() => {
    const p = this.product();
    const max = p.isPreSale ? getPrepurchaseStock(p) : p.stock;
    return this.quantity() < max;
  });

  protected discountPercent = computed(() => calcDiscountPercent(this.product()));

  protected formattedPrice = computed(() => formatPrice(this.product()));

  protected formattedOriginalPrice = computed(() => formatOriginalPrice(this.product()));

  protected imageAlt = computed(
    () => `${this.product().name} — ${this.product().setName}`
  );

  protected variantLabel = computed(() => VARIANT_LABELS[this.product().variant]);

  protected conditionLabel = computed(() => formatCondition(this.product().condition));
  protected conditionTone = computed(() => getConditionTone(this.product().condition));

  protected showWishlistOverlay = computed(() =>
    this.variant() === 'wishlist' && this.isOutOfStock() && this.isNotifyActive()
  );

  protected isInCompare = computed(() =>
    this.compareService.isSelected(this.product().id),
  );

  protected canAddToCompare = computed(
    () => this.isInCompare() || this.compareService.canAdd(),
  );

  protected compareTooltip = computed(() => {
    if (this.isInCompare()) return 'Quitar del comparador';
    if (!this.canAddToCompare()) return `Máximo ${this.compareService.maxItems} productos`;
    return 'Añadir al comparador';
  });

  protected onCompareToggle(event: Event): void {
    event.stopPropagation();
    if (!this.canAddToCompare()) return;
    this.compareService.toggle(this.product());
  }

  protected onImageClick(event: Event): void {
    event.stopPropagation();
    this.zoomOpen.emit(this.product());
  }

  protected increment(event: Event): void {
    event.stopPropagation();
    if (this.canIncrement()) {
      this.quantity.update(q => q + 1);
    }
  }

  protected decrement(event: Event): void {
    event.stopPropagation();
    if (this.canDecrement()) {
      this.quantity.update(q => q - 1);
    }
  }

  protected onAddToCart(event: Event): void {
    event.stopPropagation();
    const qty = this.viewMode() === 'list' ? this.quantity() : 1;
    this.addToCart.emit({ product: this.product(), quantity: qty });

    if (this.viewMode() === 'list') {
      this.justAdded.set(true);
      setTimeout(() => this.justAdded.set(false), 1500);
    }
  }

  /**
   * CTA "Reservar" para productos en precompra. NO va al carrito: el padre
   * debe escuchar este output y abrir el `PrepurchaseModalService`. La
   * cantidad seleccionada en modo list se pasa al modal vía initialQty.
   */
  protected onReserve(event: Event): void {
    event.stopPropagation();
    this.reserveClick.emit(this.product());
  }

  protected onPrepurchaseSoldOutClick(event: Event): void {
    event.stopPropagation();
    this.toastService.show({
      type: 'info',
      message: PREPURCHASE_SOLD_OUT_MESSAGE,
    });
  }

  protected onNotify(event: Event): void {
    event.stopPropagation();
    if (this.isNotifyActive()) {
      this.notifyCancel.emit(this.product());
    } else {
      this.notifyRequest.emit(this.product());
    }
  }

  protected onCardClick(): void {
    this.cardClick.emit(this.product());
  }
}
